import { toast } from "sonner";

// A non-PDF report is a deck/sheet with no inline renderer, so tapping it kicks
// off a native file download. Reports of thousands of jobsite photos are
// routinely 100MB+, and on an installed iOS PWA the download happens inside a
// chrome-less viewer that covers the app with a blank screen for the whole
// transfer, then surfaces a handoff sheet "out of nowhere". We can't show
// progress inside that viewer, so instead we flash a heads-up toast *before*
// handing off, then wait a beat so the toast actually paints before the viewer
// slides up and hides our UI. The delay is the whole point — without it the
// toast never gets a frame on screen on iOS PWA.
const DOWNLOAD_TOAST_COPY = "Preparing your download. It'll appear shortly.";
const DOWNLOAD_TOAST_DELAY_MS = 1000;

/**
 * Fire a native download for `url` by clicking a transient `<a download>`.
 * Mirrors the passive link the row used to render — same behaviour on every
 * platform (S3 attachment URL in prod, same-origin file in local dev) — but
 * triggered programmatically so we can sequence it after the toast.
 */
function triggerDownload(url: string, filename: string) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * The heads-up toast, then the native download a beat later. Shared by every
 * place a non-PDF report file is handed over, so the copy and the delay cannot
 * drift between them. See [[report-item.spec]] § Tappable area.
 */
export function startReportDownload(url: string, filename: string) {
  toast(DOWNLOAD_TOAST_COPY);
  window.setTimeout(() => {
    triggerDownload(url, filename);
  }, DOWNLOAD_TOAST_DELAY_MS);
}
